import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import useProfily from 'hooks/useProfily';

import { ISeznamPrikaz, PrikazSeznamGet, PrikazSeznamPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany, dokumentIsinApi, dokumentOsobyApi } from 'services/dokumenty/smlouva';
import { ETypPrevodu } from 'services/specialniSluzby/uePozadavky';

import { EPodTypDokumentu, ETypZpusobPredani } from 'enums/dokumentyEnums';
import { EEvidenceCp } from 'enums/isin/evidenceCp';

import {
    protiZaplaceniDltMenaSelect,
    protiZaplaceniMenaSelect,
    protiZaplaceniTuzemskaMenaSelect
} from 'data/codebooks/dokumenty/protizaplaceniMenaSelect';
import {
    typPrevoduNonDltExtra,
    typPrevoduPrikaz,
    zbusopPredaniDfp,
    zpusobPredaniDfpProtiExtra,
    zpusobPredaniDvpDefault,
    zpusobPredaniDvpNonDltExtra
} from 'data/codebooks/dokumenty/zpusobPredani';

import { cisloUctuValidation, kodBankyValidation, predcisliValidation } from 'utils/validations/bankUcetValidation';
import bicValidation from 'utils/validations/bicValidation';
import ibanValidation from 'utils/validations/ibanValidation';

import Osoby from '../Osoby';
import Podpisy from '../Podpisy';
import { getDataIsins } from './Zruseni';

interface ISeznamFirstStep {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
    setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setData: React.Dispatch<React.SetStateAction<ISeznamPrikaz | undefined>>;
    data?: ISeznamPrikaz;
    signingDisabled: boolean;
}

const SeznamFirstStep: React.FC<ISeznamFirstStep> = ({
    nextStep,
    zadostID,
    prikazyData,
    setCurrentStep,
    setData,
    data,
    setLoading,
    signingDisabled
}) => {
    const { setLevel } = useProfily();
    const { dokumenty } = useContext(Context);
    const [form] = Form.useForm();
    const [, rerender] = useState<boolean>(false);
    const [bicErr, setBicErr] = useState<boolean>(false);
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        dokumenty?.dokumentyPrava
            ? getDataIsins(dokumenty) // .filter(e => JSON.parse(e.data).evidencecp !== EEvidenceCp.CDCPDLT)
                  .map(e => {
                      return {
                          labelcz: e.isin,
                          value: e.isin
                      };
                  })
            : []
    );
    const [selectedIsin, setSelectIsin] = useState<any>();
    const [adresy, setAdresy] = useState<string[]>(['']);
    const sezUpisDataSet = (dokumenty?.dokumentyPrava?.prikazy || []).filter(
        e => e.typprikazu === (dokumenty?.selectedZadost?.podtyp! as any)
    )[0]?.sezupis;
    const selectedIsinData = selectedIsin ? sezUpisDataSet?.filter(e => e.isin === form.getFieldValue('isin')) : null;

    const isDlt = !selectedIsin?.evidencecp || selectedIsin?.evidencecp === EEvidenceCp.CDCPDLT;

    useEffect(() => {
        if (!selectedIsin && data?.isin) {
            setSelectIsin(JSON.parse(getDataIsins(dokumenty).filter(e => e.isin === data.isin)[0].data));
            form.setFieldsValue({
                ...data,
                typplatby: data?.typplatby
            });
            const adresyArr = [];
            data.adrprij1 && adresyArr.push(data.adrprij1);
            data.adrprij2 && adresyArr.push(data.adrprij2);
            data.adrprij3 && adresyArr.push(data.adrprij3);
            adresyArr.length && setAdresy(adresyArr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (selectedIsin) {
            form.resetFields(['typprevodu', 'zpuspredvp', 'zpuspredfp']);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        if (!dataIsins.length) {
            dokumentIsinApi().then(resp => {
                setLevel('dokumenty', {
                    ...dokumenty,
                    dokumentyPrava: resp,
                    dokumentyMenuReady: true
                });
            });
            setCurrentStep(3);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {data && prikazyData && (
                <>
                    {/* displayMode === 'soubor' ? (
                        SouborRender()
                    ) : displayMode === 'ucet' ? (
                        DfpUcetRender()
                    ) : ( */}
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            if (
                                values.typplatby === 'TUP' ||
                                (!values.bicprij && values.nazprij && values.adrprij1) ||
                                (values.bicprij && !values.nazprij && !values.adrprij1)
                            ) {
                                setLoading(true);
                                const adresyObj: { [key: string]: string } = {};
                                adresy.length &&
                                    adresy.map(
                                        (_e, i) =>
                                            (adresyObj[`adrprij${i + 1}` as keyof typeof adresyObj] =
                                                values[`adrprij${i + 1}`])
                                    );
                                const dataToSend = {
                                    ...(data.souhlas && {
                                        souhlas: true
                                    }),
                                    ...adresyObj,
                                    ...(values as any),
                                    ...(values.iban && {
                                        iban: values.iban.split(' ').join('')
                                    })
                                };
                                delete dataToSend.idmbSpolecnost;
                                delete dataToSend.dicdphSpolecnost;
                                delete dataToSend.nazevSpolecnost;
                                delete dataToSend.leiSpolecnost;
                                setData(dataToSend);
                                PrikazSeznamPut(zadostID, dataToSend)
                                    .then(() => nextStep())
                                    .finally(() => setLoading(false));
                            } else {
                                setBicErr(true);
                            }
                        }}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                            disabled={signingDisabled}
                            onChange={val => {
                                setSelectIsin(JSON.parse(getDataIsins(dokumenty).filter(e => e.isin === val)[0].data));
                                setLevel('dokumenty', {
                                    ...dokumenty,
                                    selectedZadost: {
                                        ...dokumenty?.selectedZadost!,
                                        isin: val
                                    }
                                });
                            }}
                        />
                        <SelectComponent
                            name="datum"
                            labelParagraph={'PRIKAZ_PREVOD_DATUM'}
                            defaultValue={data?.datum}
                            data={(sezUpisDataSet || [])
                                .filter(e => e.isin === form.getFieldValue('isin'))
                                .map(e => {
                                    return {
                                        value: e.datum,
                                        labelcz: dayjs(e.datum).format('DD.MM.YYYY')
                                    };
                                })}
                            isFormItem={true}
                            disabled={signingDisabled}
                            onChange={val => {
                                const selectedDataSet = sezUpisDataSet?.filter(e => e.datum === val)[0];
                                setData(selectedDataSet);
                                setTimeout(() => {
                                    form.setFieldsValue(selectedDataSet);
                                    rerender(pv => !pv);
                                }, 50);
                            }}
                        />

                        {data && form.getFieldValue('datum') && form.getFieldValue('isin') && (
                            <>
                                <Row>
                                    <Paragraph
                                        text="*"
                                        style={{
                                            color: '#ff4d4f',
                                            fontSize: 23,
                                            position: 'relative',
                                            top: 4,
                                            marginRight: 2
                                        }}
                                    />
                                    <Title level={5} text="DOKUMENT_DODATEK_CP_TITLE" />
                                </Row>
                                <Paragraph
                                    text="DOKUMENT_DODATEK_CP_TEXT"
                                    style={{
                                        display: 'block',
                                        marginBottom: 10
                                    }}
                                />
                                <SelectComponent
                                    name="typprevodu"
                                    isFormItem={true}
                                    data={[...typPrevoduPrikaz, ...(!isDlt ? typPrevoduNonDltExtra : [])]}
                                    defaultValue={data.typprevodu}
                                    onChange={() => {
                                        /*    if (val === ETypPrevodu.PROTIABEZZAPL) {
                                 setZpuspredaniDVP(ETypZpusobPredani.PARSYST);
                                setZpuspredaniDFP(ETypZpusobPredani.MD);
                                  form.setFieldsValue({
                                    zpuspredvp: ETypZpusobPredani.PARSYST,
                                    zpuspredfp: ETypZpusobPredani.MD
                                }); 
                            } else {*/

                                        //   }
                                        /* setTimeout(() => {
                                            form.setFieldsValue({
                                                zpuspredvp: undefined,
                                                zpuspredfp: undefined
                                            });
                                        }, 100); */
                                        rerender(pv => !pv);
                                    }}
                                    disabled={
                                        (selectedIsinData?.length &&
                                            selectedIsinData[0].typprevodu === ETypPrevodu.PROTIABEZZAPL) ||
                                        data.typprevodu === ETypPrevodu.BEZZAPLACENI ||
                                        signingDisabled ||
                                        (form.getFieldValue('zpuspredvp') !== ETypZpusobPredani.MD &&
                                            form.getFieldValue('zpuspredvp') !== ETypZpusobPredani.MDADMIN)
                                    }
                                />
                                {(form.getFieldValue('typprevodu') === ETypPrevodu.PROTIZAPLACENI ||
                                    form.getFieldValue('typprevodu') === ETypPrevodu.PROTIABEZZAPL) && (
                                    <SelectComponent
                                        name="zpuspredvp"
                                        labelParagraph="DOKUMENT_DODATEK_CP_SPUSOB_PRIKAZ_DVP"
                                        isFormItem={true}
                                        data={[
                                            ...zpusobPredaniDvpDefault,
                                            ...(dokumenty?.selectedZadost?.podtyp !== EPodTypDokumentu.DODATEKDLT &&
                                            dokumenty?.selectedZadost?.podtyp !== EPodTypDokumentu.NAVYSENIDLT &&
                                            form.getFieldValue('typprevodu') !== ETypPrevodu.PROTIABEZZAPL
                                                ? zpusobPredaniDvpNonDltExtra
                                                : [])
                                        ]}
                                        defaultValue={data.zpuspredvp}
                                        onChange={val => {
                                            rerender(pv => !pv);
                                            if (form.getFieldValue('typprevodu') === ETypPrevodu.PROTIABEZZAPL) {
                                                form.setFieldsValue({
                                                    zpuspredfp: val
                                                });
                                            }
                                        }}
                                        disabled={true}
                                    />
                                )}
                                {form.getFieldValue('typprevodu') === ETypPrevodu.PROTIABEZZAPL && (
                                    <SelectComponent
                                        name="zpuspredfp"
                                        labelParagraph="DOKUMENT_DODATEK_CP_SPUSOB_PRIKAZ_DFP"
                                        isFormItem={true}
                                        data={[
                                            ...zbusopPredaniDfp,
                                            ...(dokumenty?.selectedZadost?.podtyp !== EPodTypDokumentu.DODATEKDLT &&
                                            dokumenty?.selectedZadost?.podtyp !== EPodTypDokumentu.NAVYSENIDLT &&
                                            (!form.getFieldValue('vicupisovatelu') ||
                                                form.getFieldValue('vicupisovatelu') !== 'true') &&
                                            form.getFieldValue('typprevodu') !== ETypPrevodu.PROTIABEZZAPL
                                                ? zpusobPredaniDfpProtiExtra
                                                : [])
                                        ]}
                                        disabled={true}
                                        defaultValue={data.zpuspredfp || data.zpuspredvp}
                                        onChange={() => {
                                            rerender(pv => !pv);
                                        }}
                                    />
                                )}
                                {form.getFieldValue('zpuspredvp') !== ETypZpusobPredani.BURZA && (
                                    <InputNumberComponent
                                        name="celkcastka"
                                        label="DOKUMENT_DODATEK_DVP_CASTKA_CP"
                                        initialValue={data?.celkcastka}
                                        maxLength={12}
                                        precision={2}
                                        disabled={signingDisabled}
                                    />
                                )}
                                {/* <InputNumberComponent
                                    label="DOKUMENT_DODATEK_DVP_POCET_UPISOVATELU"
                                    name="celkpocupis"
                                    initialValue={data?.celkpocupis}
                                    precision={0}
                                    disabled={signingDisabled}
                                />*/}
                                <SelectComponent
                                    isFormItem={true}
                                    data={[
                                        {
                                            labelcz: 'PLATBA_TUP',
                                            value: 'TUP'
                                        },
                                        ...(!isDlt
                                            ? [
                                                  {
                                                      labelcz: 'PLATBA_ZAP',
                                                      value: 'ZAP'
                                                  }
                                              ]
                                            : [])
                                    ]}
                                    name={'typplatby'}
                                    labelParagraph={'TYP_PLATBY'}
                                    defaultValue={data.typplatby}
                                    onChange={val => {
                                        form.setFieldsValue({
                                            mena: val === 'TUP' ? 'CZK' : undefined
                                        });
                                        rerender(pv => !pv);
                                    }}
                                    disabled={true}
                                />
                                {form.getFieldValue('typplatby') && (
                                    <SelectComponent
                                        name={'mena'}
                                        labelParagraph="MENA_VYPORADANI"
                                        isFormItem={true}
                                        data={
                                            form.getFieldValue('typplatby') === 'TUP'
                                                ? protiZaplaceniTuzemskaMenaSelect
                                                : isDlt
                                                  ? protiZaplaceniDltMenaSelect
                                                  : protiZaplaceniMenaSelect
                                        }
                                        defaultValue={form.getFieldValue('typplatby') === 'TUP' ? 'CZK' : data?.mena}
                                        disabled={true}
                                        onChange={() => rerender(pv => !pv)}
                                    />
                                )}
                                <Title
                                    level={5}
                                    text="DOKUMENT_DODATEK_DVP_CASTKA_TITLE"
                                    style={{
                                        display: 'inline-block'
                                    }}
                                    dividerTitle={true}
                                />
                                {form.getFieldValue('typplatby') === 'TUP' && (
                                    <>
                                        <InputComponent
                                            label="PREDCISLI_UCTU"
                                            name="predcisli"
                                            initialValue={data?.predcisli}
                                            notRequired={true}
                                            maxLength={6}
                                            rules={predcisliValidation()}
                                            disabled={signingDisabled}
                                        />
                                        <InputComponent
                                            label="CISLO_UCTU"
                                            name="cislouctu"
                                            initialValue={data?.cislouctu}
                                            maxLength={10}
                                            rules={cisloUctuValidation()}
                                            disabled={signingDisabled}
                                        />
                                        <InputComponent
                                            label="CISLO_BANKY"
                                            name="banka"
                                            initialValue={data?.banka}
                                            maxLength={4}
                                            rules={kodBankyValidation()}
                                            disabled={signingDisabled}
                                        />
                                    </>
                                )}
                                {form.getFieldValue('typplatby') === 'ZAP' && (
                                    <>
                                        <InputComponent
                                            label="IBAN_PRIJEMCE"
                                            name={'iban'}
                                            maxLength={250}
                                            //   notRequired={true}
                                            initialValue={data.iban}
                                            rules={form.getFieldValue('mena') === 'EUR' ? ibanValidation() : undefined}
                                            disabled={signingDisabled}
                                        />
                                        <InputComponent
                                            label="BIC_BANKY"
                                            name={'bic'}
                                            initialValue={data.bic}
                                            rules={bicValidation()}
                                            disabled={signingDisabled}
                                        />
                                        <InputComponent
                                            label="NAZEV_PRIJEMCE"
                                            name={'nazprij'}
                                            initialValue={data.nazprij}
                                            disabled={signingDisabled}
                                            notRequired={true}
                                            onChange={() => setBicErr(false)}
                                        />
                                        {adresy.map((e, i) => (
                                            <InputComponent
                                                label="ADRESA_PRIJEMCE"
                                                key={`adrprij${i + 1}`}
                                                name={`adrprij${i + 1}`}
                                                maxLength={35}
                                                notRequired={true}
                                                disabled={signingDisabled}
                                                onChange={() => setBicErr(false)}
                                                initialValue={e}
                                                labelExtra={
                                                    (i === adresy.length - 1 || (i === 0 && adresy.length < 3)) &&
                                                    !signingDisabled ? (
                                                        <Button
                                                            id="c2PlusAltBtn"
                                                            htmlType="button"
                                                            type="primary"
                                                            style={{
                                                                display: 'inline-block',
                                                                marginLeft: 20,
                                                                width: 50
                                                            }}
                                                            disabled={false}
                                                            icon={i === 0 ? <PlusOutlined /> : <MinusOutlined />}
                                                            onClick={() => {
                                                                if (i === 0) setAdresy(pv => [...pv, '']);
                                                                else {
                                                                    form.setFieldsValue({
                                                                        [`adrprij${i + 1}`]: undefined
                                                                    });
                                                                    setAdresy(pv =>
                                                                        pv.filter((_e, index) => i !== index)
                                                                    );
                                                                }
                                                            }}
                                                        />
                                                    ) : null
                                                }
                                            />
                                        ))}
                                        <InputComponent
                                            label="PRIJEMCE_BIC"
                                            name={'bicprij'}
                                            initialValue={data.bicprij}
                                            rules={bicValidation()}
                                            disabled={signingDisabled}
                                            notRequired={true}
                                            onChange={() => setBicErr(false)}
                                        />
                                    </>
                                )}
                                <PrilohyTable zadostID={zadostID} disabled={signingDisabled} />
                            </>
                        )}
                        <Row>
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    extramessage={bicErr ? 'DOKUMENT_DODATEK_DVP_ADRESA_BIC_ERR' : ''}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                    {/* ) */}
                </>
            )}
        </>
    );
};

interface ISeznamPodminky {
    nextStep: () => void;
    prevStep: () => void;
    signingDisabled: boolean;
    data: ISeznamPrikaz;
    setData: React.Dispatch<React.SetStateAction<ISeznamPrikaz | undefined>>;
    zadostID: number;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const SeznamPodminky: React.FC<ISeznamPodminky> = ({
    nextStep,
    prevStep,
    signingDisabled,
    data,
    setData,
    zadostID,
    setLoading
}) => {
    const [isChecked, setIsChecked] = useState<boolean>(signingDisabled);
    const [form] = Form.useForm();

    return (
        <FormWrapper outForm={form} onFinish={() => {}}>
            <Paragraph
                text="DOKUMENT_DODATEK_SHRNUTI_DLT_1_UPIS"
                style={{
                    display: 'block',
                    fontWeight: 'bold'
                }}
            />
            <Paragraph
                text="PRIKAZ_SEZNAM_PODMINKY_1"
                style={{
                    display: 'block',
                    marginTop: 16
                }}
            />
            <Paragraph
                text="PRIKAZ_SEZNAM_PODMINKY_2"
                style={{
                    display: 'block',
                    marginTop: 16
                }}
            />
            <Paragraph
                text="PRIKAZ_SEZNAM_PODMINKY_3"
                style={{
                    display: 'block',
                    marginTop: 16
                }}
            />
            <Paragraph
                text="PRIKAZ_SEZNAM_PODMINKY_3A"
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text="PRIKAZ_SEZNAM_PODMINKY_3B"
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text="PRIKAZ_SEZNAM_PODMINKY_3C"
                style={{
                    display: 'block'
                }}
            />
            <Paragraph
                text="PRIKAZ_SEZNAM_PODMINKY_3X"
                style={{
                    display: 'block'
                }}
            />
            <Row
                style={{
                    marginTop: 26
                }}
            >
                <CheckboxComponent
                    name="souhlas"
                    checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                    onChange={() => setIsChecked(pv => !pv)}
                    disabled={signingDisabled}
                    checked={isChecked}
                    defaultChecked={isChecked}
                />
            </Row>
            <Row className="button-row">
                <ButtonComponent buttontext="ZPET" type="default" onClick={() => prevStep()} id="isinZpetBtn" />
                <ButtonComponent
                    buttontext="POKRACOVAT"
                    onClick={() => {
                        nextStep();

                        if (!data.souhlas) {
                            PrikazSeznamPut(zadostID, {
                                ...data,
                                souhlas: true
                            })
                                .then(() => {
                                    nextStep();
                                    setData({
                                        ...data,
                                        souhlas: true
                                    });
                                })
                                .finally(() => setLoading(false));
                        }
                    }}
                    id="isinPokracovatBtn"
                    disabled={!isChecked}
                />
            </Row>
        </FormWrapper>
    );
};

interface ISeznam {
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const Seznam: React.FC<ISeznam> = ({ zadostID, prikazyData }) => {
    const { confirm } = Modal;
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [data, setData] = useState<ISeznamPrikaz>();
    const [loading, setLoading] = useState<boolean>(false);
    const signingDisabled = !!data?.souhlas;

    useEffect(() => {
        setLoading(true);
        PrikazSeznamGet(zadostID)
            .then(resp => {
                dokumentOsobyApi(zadostID, true)
                    .then(response => response.osoby.some(e => !!e.podepsano) && setCurrentStep(3))
                    .finally(() => setLoading(false));
                setData(resp);
            })
            .catch(() => {
                setData({} as ISeznamPrikaz);
                setLoading(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    const next = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep + 1);
    };

    const prev = () => {
        window.scrollTo(0, 0);
        setCurrentStep(currentStep - 1);
    };

    const steps = [
        {
            title: '',
            content: (
                <SeznamFirstStep
                    nextStep={next}
                    zadostID={zadostID!}
                    prikazyData={prikazyData}
                    setCurrentStep={setCurrentStep}
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    signingDisabled={signingDisabled}
                />
            )
        },
        {
            title: 'DOKUMENT_DODATEK_SHRNUTI_TITLE',
            content: (
                <SeznamPodminky
                    nextStep={next}
                    prevStep={prev}
                    signingDisabled={signingDisabled}
                    data={data!}
                    setData={setData}
                    setLoading={setLoading}
                    zadostID={zadostID}
                />
            )
        },
        {
            title: '',
            content: <Osoby nextStep={next} prevStep={prev} zadostID={zadostID!} isDodatek={false} />
        },
        {
            title: '',
            content: <Podpisy prevStep={prev} zadostID={zadostID!} showPreControl={false} isPrikaz={true} />
        }
    ];

    return (
        <LoadingWrapper loading={loading}>
            {steps.length - 3 > currentStep && data?.souhlas && (
                <Button
                    style={{
                        top: '30%',
                        right: 10,
                        position: 'fixed',
                        zIndex: 999
                    }}
                    type="primary"
                    onClick={() =>
                        confirm({
                            title: <TranslateText>DOKUMENTY_OSOBY_ZMENIT</TranslateText>,
                            cancelText: <TranslateText>NE</TranslateText>,
                            okText: <TranslateText>ANO</TranslateText>,
                            onOk() {
                                setLoading(true);
                                PrikazSeznamPut(zadostID!, {
                                    ...data!,
                                    souhlas: false
                                })
                                    .then(() => {
                                        setData({
                                            ...data!,
                                            souhlas: false
                                        });
                                    })
                                    .finally(() => setLoading(false));
                            }
                        })
                    }
                >
                    <TranslateText>POVOLIT_EDITACI</TranslateText>
                </Button>
            )}
            <Row gutter={8}>
                <Col span={24}>{steps[currentStep].content}</Col>
            </Row>
        </LoadingWrapper>
    );
};

export default Seznam;
